/* eslint-disable no-unneeded-ternary */
/**
 * External dependencies
 */
import React, { memo, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

/**
 * Internal dependencies
 */
import { i18ns } from '@naruto/naruto-models/src/core/i18n';
import Modal from '@naruto/naruto-components/src/base/v1/Modal';
import Lang from './lang';
import { ModalDialogProps, ConfirmItemObject } from './types';

/* Confirm Item */
function ConfirmItem(props: ConfirmItemObject): React.ReactElement {
  /* Global & Local States */
  const { confirm, removeConfirm } = props;

  /**
   * 確認訊息視窗設定物件
   *
   * @typedef {Object} MessageConfirmObject
   *
   * @property typeMessage 訊息類型
   * @property typeTitle 訊息標題
   * @property typeContent 訊息內容
   * @property [showConfirmButton=true] 是否顯示確認按鈕
   * @property [showCancelButton=true] 是否顯示取消按鈕
   * @property [confirmText=''] 確認按鈕文字
   * @property [cancelText=''] 取消按鈕文字
   * @property [confirmTextColor='inherit'] 確認按鈕文字顏色
   * @property [cancelTextColor='inherit'] 取消按鈕文字顏色
   * @property [confirmTextVariation='text'] 確認按鈕樣式
   * @property [cancelTextVariation='text'] 取消按鈕樣式
   * @property [onConfirm] 確認按鈕點擊事件
   * @property [onCancel] 取消按鈕點擊事件
   *
   */
  const {
    typeTitle,
    typeContent = '',
    showConfirmButton = true,
    confirmText = '',
    confirmTextVariation = 'text',
    confirmTextColor = 'inherit',
    onConfirm,
    showCancelButton = true,
    cancelText = '',
    cancelTextVariation = 'text',
    cancelTextColor = 'inherit',
    onCancel
  } = confirm;

  /* Functions */
  const onConfirmClick = (): void => {
    if (onConfirm) onConfirm();
    removeConfirm();
  };
  const onCancelClick = (): void => {
    if (onCancel) onCancel();
    removeConfirm();
  };

  /* Main */
  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative'
      }}
    >
      <Box
        sx={{
          width: '100%',
          marginBottom: '8px',
          position: 'relative',
          fontWeight: 'bold !important',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Typography sx={{ lineHeight: '36px !important' }} variant="h4" color="textPrimary">
          {typeTitle}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          minHeight: '23px',
          marginBottom: '24px',
          position: 'relative'
        }}
      >
        <Typography variant="body1" color="textPrimary" style={{ whiteSpace: 'pre-wrap' }}>
          {typeContent}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          minHeight: '23px',
          display: 'flex',
          justifyContent: 'flex-end',
          position: 'relative'
        }}
      >
        {showCancelButton && (
          <Button
            sx={{
              ml: 4,
              position: 'relative'
            }}
            variant={cancelTextVariation}
            color={cancelTextColor}
            onClick={onCancelClick}
          >
            {cancelText ? cancelText : i18ns.__(Lang.CANCEL_BUTTON)}
          </Button>
        )}
        {showConfirmButton && (
          <Button
            sx={{
              ml: 4,
              position: 'relative'
            }}
            className="test-confirmItemActionButton"
            variant={confirmTextVariation}
            color={confirmTextColor}
            onClick={onConfirmClick}
          >
            {confirmText ? confirmText : i18ns.__(Lang.CONFIRM_BUTTON)}
          </Button>
        )}
      </Box>
    </Box>
  );
}

function ModalConfirm(props: ModalDialogProps): React.ReactElement {
  /* Global & Local States */
  const { list, removeConfirm } = props;
  /* Views */
  const RenderMessages = useMemo(() => {
    if (list.length > 0) {
      return <ConfirmItem confirm={list[0]} removeConfirm={removeConfirm} />;
    }
    return <React.Fragment />;
  }, [list]);
  const RenderIsMessageOpen = useMemo(() => {
    if (list.length > 0) {
      return true;
    }
    return false;
  }, [list]);
  /* Main */
  return (
    <Modal
      open={RenderIsMessageOpen}
      onClose={removeConfirm}
      typeSize="sm"
      typeTheme="custom"
      disableCancelButton
      disableConfirmButton
    >
      {RenderMessages}
    </Modal>
  );
}

export default memo(ModalConfirm);
